import { observable, runInAction } from "mobx"

import { withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"

import type { GitHubStatus, PR_Project2 } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"


export class ProjectGitHubIntegrationModel extends Model {
  constructor(private readonly context: OrgContext, readonly project: PR_Project2) {
    super("GitHubIntegrationModel")
    void this.subscribeToGitHubStatus()

    // Preload GitHub Connect URL early
    void this.getGitHubConnectUrl()
  }

  readonly gitHubStatus = observable.box<GitHubStatus | null>(null)
  private gitHubConnectionUrl: string | null = null

  private async subscribeToGitHubStatus() {
    try {
      runInAction(() => this.isLoading.set(true))
      const subscription = await this.context.queries.getGitHubStatus(this.project.key, this.project.repository.name)
      runInAction(() => {
        this.gitHubStatus.set(subscription.initial)
      })

      const channel = subscription.channel
      this.lifetime.add(channel.subscribe(status => {
        runInAction(() => this.gitHubStatus.set(status))
      }), "GitHubIntegration:subscribeToGitHubStatus")
    } catch (e) {
      log.error("Failed to subscribe to GitHub status:", e)
    } finally {
      runInAction(() => this.isLoading.set(false))
    }
  }

  readonly connectToGitHub = async () => {
    if (this.gitHubConnectionUrl != null) return openLinkInNewWindow(this.gitHubConnectionUrl)

    const url = await withGlobalLoader(this.context, this.getGitHubConnectUrl())
    url && openLinkInNewWindow(url)
  }

  readonly configureGitHub = async () => {
    if (this.gitHubConnectionUrl != null) return openLinkInNewWindow(this.gitHubConnectionUrl)

    const url = await withGlobalLoader(this.context, this.getGitHubConnectUrl())
    url && openLinkInNewWindow(url)
  }

  private async getGitHubConnectUrl() {
    if (this.gitHubConnectionUrl != null) {
      return this.gitHubConnectionUrl
    }
    try {
      const url = await this.context.queries.apiClient.startGitHubOAuthFlow()
      this.gitHubConnectionUrl = url
      return url
    } catch {
      return null
    }
  }
}

const log = getLogger("GitHubIntegrationModel")
